"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useInjectStyles } from "../utils/useInjectStyles";

export type ToastVariant = "info" | "success" | "warning" | "danger";

export interface ToastItem {
  id: number;
  message: ReactNode;
  variant: ToastVariant;
  /** 0 = 不自動消失（網路中斷這類「使用者必須知道」的訊息用）。 */
  durationMs: number;
}

export interface ShowToastOptions {
  variant?: ToastVariant;
  /** 省略時依 variant 取預設：danger 6s、warning 5s、其餘 3.5s。0 = 不自動消失。 */
  durationMs?: number;
}

interface ToastContextValue {
  show: (message: ReactNode, options?: ShowToastOptions) => number;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

/**
 * 各 variant 的預設停留時間。
 *
 * danger 給 6 秒而非 3.5 秒：失敗訊息通常帶有原因（「資料已被異動，請重新整理後再試」），
 * 3.5 秒對一句十幾個字的中文句子太短，使用者常常來不及讀完就消失。
 * warning 介於兩者之間。
 */
const DEFAULT_DURATION: Record<ToastVariant, number> = {
  success: 3500,
  info: 3500,
  warning: 5000,
  danger: 6000,
};

/**
 * 語意圖示。
 *
 * 為什麼一定要有：只靠顏色分辨成功/失敗/警告，色弱使用者無法區分。
 * 這個專案的 Badge 元件已經用「形狀圓點」（實心圓/空心圓/方塊）解決過同一個問題，
 * Toast 沿用同樣的思路——形狀本身就帶語意，不依賴色覺。
 */
const VARIANT_ICON: Record<ToastVariant, string> = {
  success: "✓",
  danger: "✕",
  warning: "!",
  info: "i",
};

/**
 * 哪些 variant 需要「插播」。
 *
 * role="alert" 內建 aria-live="assertive"，會打斷螢幕閱讀器目前的朗讀佇列。
 * 判準是「使用者接下來的動作是否依賴這則訊息」：
 *   失敗/警告 → 依賴（要先知道哪裡錯了才能決定下一步）→ assertive
 *   成功/資訊 → 不依賴（原本要做的下一步不受影響）→ polite
 *
 * 改版前所有 variant 一律 role="status" + 單一 aria-live="polite" 容器，
 * 等於失敗訊息永遠不會被插播，螢幕閱讀器使用者可能整個錯過。
 */
function isAssertive(variant: ToastVariant): boolean {
  return variant === "danger" || variant === "warning";
}

export function ToastProvider({ children }: { children: ReactNode }) {
  useInjectStyles();
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const idRef = useRef(0);
  // 計時器要能被 hover 暫停後重啟，故存起來而不是 fire-and-forget
  const timersRef = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: number) => {
    const t = timersRef.current.get(id);
    if (t) {
      clearTimeout(t);
      timersRef.current.delete(id);
    }
    setToasts((prev) => prev.filter((x) => x.id !== id));
  }, []);

  const arm = useCallback(
    (id: number, duration: number) => {
      if (duration <= 0) return;
      const t = setTimeout(() => dismiss(id), duration);
      timersRef.current.set(id, t);
    },
    [dismiss],
  );

  const show = useCallback(
    (message: ReactNode, options?: ShowToastOptions) => {
      const id = ++idRef.current;
      const variant = options?.variant ?? "info";
      const durationMs = options?.durationMs ?? DEFAULT_DURATION[variant];
      setToasts((prev) => [...prev, { id, message, variant, durationMs }]);
      arm(id, durationMs);
      return id;
    },
    [arm],
  );

  // 卸載時清掉所有計時器，避免對已卸載的元件 setState
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach((t) => clearTimeout(t));
      timers.clear();
    };
  }, []);

  /** 滑鼠移入時暫停倒數——長句子不該在使用者正在讀的時候消失。 */
  const pause = useCallback((id: number) => {
    const t = timersRef.current.get(id);
    if (t) {
      clearTimeout(t);
      timersRef.current.delete(id);
    }
  }, []);

  const resume = useCallback(
    (item: ToastItem) => {
      // 移出後重新給完整時間，而不是接續剩餘秒數：使用者剛把滑鼠移開，
      // 這時只剩 0.3 秒就消失反而更難讀。
      if (!timersRef.current.has(item.id)) arm(item.id, item.durationMs);
    },
    [arm],
  );

  const value = useMemo(() => ({ show, dismiss }), [show, dismiss]);

  const render = (t: ToastItem) => (
    <div
      key={t.id}
      className={`cui-toast cui-toast-${t.variant}`}
      role={isAssertive(t.variant) ? "alert" : "status"}
      onMouseEnter={() => pause(t.id)}
      onMouseLeave={() => resume(t)}
    >
      <span className="cui-toast-icon" aria-hidden>
        {VARIANT_ICON[t.variant]}
      </span>
      <span className="cui-toast-msg">{t.message}</span>
      <button type="button" className="cui-toast-close" onClick={() => dismiss(t.id)} aria-label="關閉通知">
        ✕
      </button>
    </div>
  );

  const polite = toasts.filter((t) => !isAssertive(t.variant));
  const assertive = toasts.filter((t) => isAssertive(t.variant));

  return (
    <ToastContext.Provider value={value}>
      {children}
      {/*
        兩個 viewport 而非一個：aria-live 是**容器**屬性，同一個容器不可能同時是
        polite 又是 assertive。分成兩個之後，成功訊息不打斷朗讀、失敗訊息會插播。
        視覺上兩個容器疊在同一位置（CSS 讓它們都貼右下角並共用堆疊流），
        使用者看不出有兩個。
      */}
      <div className="cui-reset cui-toast-viewport" aria-live="polite">
        {polite.map(render)}
      </div>
      <div className="cui-reset cui-toast-viewport cui-toast-viewport-assertive" aria-live="assertive">
        {assertive.map(render)}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast 必須在 <ToastProvider> 內使用");
  return ctx;
}
